import { useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ArticlesPartnerList from "src/components/Articles/ArticlesPartnerList";
import { PROD_FRONTEND_URL } from "src/config/frontendUrls";
import {
  SEO_ARTICLES_LIST_DESCRIPTION,
  SEO_ARTICLES_LIST_TITLE,
  SEO_OG_IMAGE,
  SEO_SITE_NAME,
  SEO_TWITTER_HANDLE,
  buildArticleCanonicalPath,
  buildArticlesListCanonicalUrl,
} from "src/config/seo.config";

const MONGO_OBJECT_ID_RE = /^[a-f\d]{24}$/i;

function isValidArticleMongoId(id: string): boolean {
  return MONGO_OBJECT_ID_RE.test(id.trim());
}

function isValidArticleSlug(slug: string): boolean {
  const trimmed = slug.trim();
  return trimmed.length > 0 && trimmed.length <= 200 && !isValidArticleMongoId(trimmed);
}

function ArticlesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: slugParam } = useParams<{ slug?: string }>();

  const isArticlesListRoute = !slugParam?.trim();

  // Legacy /articles?id= → /articles/:slug before Auth0 (SEO + old Instagram links).
  useEffect(() => {
    if (!isArticlesListRoute) return;
    const idParam = new URLSearchParams(location.search).get("id")?.trim();
    if (!idParam || !(isValidArticleSlug(idParam) || isValidArticleMongoId(idParam))) return;
    navigate(`${buildArticleCanonicalPath(idParam)}${location.hash}`, { replace: true });
  }, [isArticlesListRoute, location.hash, location.search, navigate]);

  // Normalize /articles/slug/ → /articles/slug (Firebase directory index adds trailing slash).
  useEffect(() => {
    const { pathname, search, hash } = location;
    if (pathname.length > 1 && pathname.endsWith("/")) {
      navigate(`${pathname.replace(/\/+$/, "")}${search}${hash}`, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    const prevHtmlOverflowY = htmlEl.style.overflowY;
    const prevBodyOverflowY = bodyEl.style.overflowY;
    const prevBodyOverscrollBehavior = bodyEl.style.overscrollBehavior;

    htmlEl.style.overflowY = "auto";
    bodyEl.style.overflowY = "auto";
    bodyEl.style.overscrollBehavior = "auto";

    return () => {
      htmlEl.style.overflowY = prevHtmlOverflowY;
      bodyEl.style.overflowY = prevBodyOverflowY;
      bodyEl.style.overscrollBehavior = prevBodyOverscrollBehavior;
    };
  }, []);

  const listCanonicalUrl = buildArticlesListCanonicalUrl(
    process.env.REACT_APP_FRONTEND_URI_WEB || PROD_FRONTEND_URL
  );

  return (
    <>
      {isArticlesListRoute ? (
        <Helmet>
          <title>{SEO_ARTICLES_LIST_TITLE}</title>
          <meta name="description" content={SEO_ARTICLES_LIST_DESCRIPTION} />
          <link rel="canonical" href={listCanonicalUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={SEO_SITE_NAME} />
          <meta property="og:title" content={SEO_ARTICLES_LIST_TITLE} />
          <meta property="og:description" content={SEO_ARTICLES_LIST_DESCRIPTION} />
          <meta property="og:url" content={listCanonicalUrl} />
          <meta property="og:image" content={SEO_OG_IMAGE} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content={SEO_TWITTER_HANDLE} />
          <meta name="twitter:title" content={SEO_ARTICLES_LIST_TITLE} />
          <meta name="twitter:description" content={SEO_ARTICLES_LIST_DESCRIPTION} />
          <meta name="twitter:image" content={SEO_OG_IMAGE} />
        </Helmet>
      ) : null}
      {/* Fixed viewport height so standalone detail can pin ArticlesSign + scroll the body (same as drawer). */}
      <Box sx={{ height: "100vh", overflow: "hidden", bgcolor: "#000000", "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
        <ArticlesPartnerList standalone />
      </Box>
    </>
  );
}

export default ArticlesPage;
